import React, { useState, useEffect } from 'react';
import { 
  Leaf, Eye, Star, Sparkles, Shield, Heart, AlertCircle, 
  Scale, Fish, Sun, Apple, Mountain, Zap, Activity, 
  Search, Clock, Droplets, PieChart 
} from 'lucide-react';
import { getApiBaseUrl } from '../config/comercio';

const ClasificacionesDieteticas = ({ 
  clasificacionPrincipal = null,
  onClasificacionPrincipalChange,
  deteccionAutomatica = null,
  disabled = false
}) => {
  const [clasificaciones, setClasificaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cargar clasificaciones desde la API
  useEffect(() => {
    const cargarClasificaciones = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const API_BASE_URL = getApiBaseUrl();
        console.log('🌱 [CLASIFICACIONES] Cargando desde:', API_BASE_URL);
        
        // Agregar timeout para evitar que se cuelgue
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 segundos
        
        const response = await fetch(`${API_BASE_URL}/clasificaciones_dieteticas.php`, {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        const data = await response.json();
        
        if (data.success) {
          setClasificaciones(data.clasificaciones);
          console.log('🌱 [CLASIFICACIONES] Cargadas:', data.clasificaciones.length);
        } else {
          throw new Error(data.message || 'Error al cargar clasificaciones');
        }
      } catch (err) {
        console.error('❌ [ERROR] Clasificaciones:', err);
        
        if (err.name === 'AbortError') {
          setError('⏰ Timeout: La API tardó más de 10 segundos en responder');
        } else if (err.message.includes('fetch')) {
          setError(`🌐 Error de conexión: No se pudo conectar a ${getApiBaseUrl()}`);
        } else {
          setError(`❌ Error: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    cargarClasificaciones();
  }, []);

  // Obtener icono por tipo de clasificación
  const getIconoClasificacion = (nombre) => {
    const nombreLower = nombre.toLowerCase();
    
    if (nombreLower.includes('vegano')) return <Leaf className="w-6 h-6 text-green-600" />;
    if (nombreLower.includes('vegetariano')) return <Heart className="w-6 h-6 text-green-500" />;
    if (nombreLower.includes('gluten') || nombreLower.includes('tacc')) return <Shield className="w-6 h-6 text-blue-600" />;
    if (nombreLower.includes('ayurvédica') || nombreLower.includes('ayurvedica')) return <Sparkles className="w-6 h-6 text-purple-600" />;
    if (nombreLower.includes('lactosa')) return <AlertCircle className="w-6 h-6 text-orange-600" />;
    if (nombreLower.includes('orgánico') || nombreLower.includes('organico')) return <Leaf className="w-6 h-6 text-green-700" />;
    if (nombreLower.includes('omnívora') || nombreLower.includes('omnivora')) return <Heart className="w-6 h-6 text-gray-600" />;
    if (nombreLower.includes('flexitariana')) return <Scale className="w-6 h-6 text-purple-600" />;
    if (nombreLower.includes('pescetariana')) return <Fish className="w-6 h-6 text-cyan-600" />;
    if (nombreLower.includes('crudivegana')) return <Sun className="w-6 h-6 text-orange-600" />;
    if (nombreLower.includes('frugívora') || nombreLower.includes('frugivora')) return <Apple className="w-6 h-6 text-red-600" />;
    if (nombreLower.includes('macrobiótica') || nombreLower.includes('macrobiotica')) return <Sparkles className="w-6 h-6 text-purple-600" />;
    if (nombreLower.includes('paleo') || nombreLower.includes('paleolítica')) return <Mountain className="w-6 h-6 text-orange-600" />;
    if (nombreLower.includes('keto') || nombreLower.includes('cetogénica')) return <Zap className="w-6 h-6 text-red-600" />;
    if (nombreLower.includes('mediterránea') || nombreLower.includes('mediterranea')) return <Sun className="w-6 h-6 text-yellow-600" />;
    if (nombreLower.includes('dash')) return <Heart className="w-6 h-6 text-red-600" />;
    if (nombreLower.includes('fodmap')) return <Activity className="w-6 h-6 text-purple-600" />;
    if (nombreLower.includes('kosher')) return <Star className="w-6 h-6 text-blue-600" />;
    if (nombreLower.includes('halal')) return <Star className="w-6 h-6 text-green-600" />;
    if (nombreLower.includes('carnívora') || nombreLower.includes('carnivora')) return <Heart className="w-6 h-6 text-red-600" />;
    if (nombreLower.includes('eliminación') || nombreLower.includes('eliminacion')) return <Search className="w-6 h-6 text-orange-600" />;
    if (nombreLower.includes('ayuno')) return <Clock className="w-6 h-6 text-gray-600" />;
    if (nombreLower.includes('alcalina')) return <Droplets className="w-6 h-6 text-cyan-600" />;
    if (nombreLower.includes('zona')) return <PieChart className="w-6 h-6 text-purple-600" />;
    if (nombreLower.includes('raw till')) return <Sun className="w-6 h-6 text-orange-600" />;
    
    return <Leaf className="w-6 h-6 text-gray-600" />;
  };

  // Verificar si una clasificación está detectada automáticamente
  const estaClasificacionDetectada = (clasificacionId) => {
    if (!deteccionAutomatica || !deteccionAutomatica.clasificacionPrincipal) return false;
    
    // Buscar la clasificación por ID en la base de datos
    const clasificacion = clasificaciones.find(c => c.id == clasificacionId);
    if (!clasificacion) return false;
    
    // Comparar por nombre con la clasificación principal detectada
    return deteccionAutomatica.clasificacionPrincipal.toLowerCase() === clasificacion.nombre.toLowerCase();
  };

  if (loading) {
    return (
      <div className="clasificaciones-dieteticas p-6 border rounded-xl bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="flex items-center justify-center space-x-3 text-gray-600">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
          <span className="text-lg">Cargando clasificaciones dietéticas...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="clasificaciones-dieteticas p-6 border rounded-xl bg-red-50 border-red-200">
        <div className="flex items-center space-x-3 text-red-600">
          <AlertCircle className="w-6 h-6" />
          <span className="text-lg">Error: {error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="clasificaciones-dieteticas">
      {/* Título principal compacto */}
      <div className="text-center">
        <h3 className="flex items-center justify-center space-x-2">
          <Leaf className="w-6 h-6 text-green-600" />
          <span>Clasificación Dietética Principal</span>
          <Leaf className="w-6 h-6 text-green-600" />
        </h3>
        
        {deteccionAutomatica && (
          <div className="deteccion-info">
            <Eye className="w-4 h-4" />
            <span>Detección Automática Activa</span>
          </div>
        )}
      </div>

      {/* Clasificación Principal optimizada */}
      <div className="clasificacion-principal">
        <label className="flex items-center space-x-1">
          <Star className="w-4 h-4 text-green-600" />
          <span>🌟 Clasificación Principal</span>
        </label>
        <select
          value={clasificacionPrincipal || ''}
          onChange={(e) => onClasificacionPrincipalChange(e.target.value || null)}
          disabled={disabled}
          className="clasificacion-dropdown"
          title="Selecciona el tipo de dieta principal del producto"
        >
          <option value="">Seleccionar clasificación principal</option>
          {clasificaciones.map((clasificacion) => (
            <option key={clasificacion.id} value={clasificacion.id} title={clasificacion.descripcion}>
              {clasificacion.nombre}
            </option>
          ))}
        </select>
        <p>
          Esta será la clasificación más importante del producto
        </p>
      </div>

      {/* Clasificación Seleccionada compacta */}
      {clasificacionPrincipal && (
        <div className="clasificacion-seleccionada">
          <div className="text-center">
            <div className="text-lg">
              ✅ Clasificación Principal Seleccionada
            </div>
            <div className="flex items-center justify-center space-x-2 mb-2">
              {getIconoClasificacion(clasificaciones.find(c => c.id == clasificacionPrincipal)?.nombre || '')}
              <span className="text-xl font-bold">
                {clasificaciones.find(c => c.id == clasificacionPrincipal)?.nombre || 'Clasificación'}
              </span>
              {estaClasificacionDetectada(clasificacionPrincipal) && (
                <span className="badge-detectado">
                  DETECTADO
                </span>
              )}
            </div>
            <div className="text-xs text-gray-600 italic">
              {clasificaciones.find(c => c.id == clasificacionPrincipal)?.descripcion || ''}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClasificacionesDieteticas;
